import React, { Component } from 'react'
import moment from 'moment-timezone'
import { connect } from 'react-redux'

import DaySelection from './DaySelection/DaySelection'
import TimeSelection from './TimeSelection/TimeSelection'
import { fetchAvailableTimes } from 'cogsworth/utils/availableTimes'
import Loading from 'cogsworth/components/Loading/Loading'
import { DEFAULT_BUSINESS_AVAILABILITY_LIMIT_DAYS } from 'cogsworth/constants'
import { getDateUrlParameter } from 'cogsworth/utils/filters'
const MAX_FETCH_DAYS = 8
const MAX_FETCH_ATTEMPTS_DAYS = 90

class MonthView extends Component {
  constructor(props){
    super(props)

    const { day, preselectedDay, timezone, calendarView } = props

    const daysCount = this.getDaysCount({ calendarView })
    const startingDate = this.getStartingDate({ day, preselectedDay, timezone, daysCount })

    this.state = {
      startingDate,
      startingDateISO: startingDate.toISOString(),
      daysCount,
      allowDaySelection: true,
      availableTimes: [],
      loading: false,
    }

    this.onNav = this.onNav.bind(this)
    this.fetch = this.fetch.bind(this)
    this.setStartingDate = this.setStartingDate.bind(this)
  }

  async componentDidMount() {
    let count = 0, maxReached = false

    do {
      await this.setStartingDate({ props: this.props, skipCount: count }).then(this.fetch)
      count ++
      if(count * this.state.daysCount > MAX_FETCH_ATTEMPTS_DAYS) {
        maxReached = true
      }
    } while(this.state.availableTimes.length === 0 && !maxReached)

    // If we found available times, set the starting date to the first available day.
    if(this.state.availableTimes.length > 0) {
      await this.setStartingDate({
        props: this.props,
        skipCount: 0,
        firstAvailableDate: this.state.availableTimes[0].time
      })
    }

    // We might need to fetch some additional days after setting the starting date to
    // the first available day.
    const lastFetched = this.state.fetchedTimes.to
    const lastDate = this.state.startingDate.clone().add(this.state.daysCount, 'days')
    if(lastDate.isAfter(lastFetched)) {
      const availableTimes = await this.fetchAvailableTimesRange({
        dateFrom: lastFetched,
        dateTo: lastDate,
      })
      this.setState({
        availableTimes: [...this.state.availableTimes, ...availableTimes],
        fetchedTimes: {
          ...this.state.fetchedTimes,
          to: lastDate,
        }
      })
    }

  }

  componentWillReceiveProps(nextProps) {
    const { preselectedDay, calendarView } = this.props
    if (preselectedDay !== nextProps.preselectedDay) {
      this.setStartingDate({ props: nextProps }).then(this.fetch)
    }

    if(calendarView !== nextProps.calendarView) {
      this.onCalendarViewChanged(nextProps.calendarView)
    }
  }

  onCalendarViewChanged(calendarView){
    const { preselectedDay, timezone } = this.props
    const daysCount = this.getDaysCount({ calendarView })
    const startingDate = this.getStartingDate({ preselectedDay, timezone, daysCount })

    this.setState({
      daysCount,
      startingDate,
      loading: true,
    }, () => {
      this.fetch()
    })
  }

  getStartingDate({ day, preselectedDay, firstAvailableDate, timezone, daysCount }) {
    let startDay = firstAvailableDate || day || preselectedDay || moment()

    // If the selected day is contain within the period between the current date
    // and the number of days to be displayed, the current date should be the initial date.
    if (day && moment(day).isBefore(moment().add(daysCount, 'days'))) {
      startDay = moment()
    }

    let startDate = moment(startDay).startOf('day')

    const startLimit = getDateUrlParameter(window.location.search, 'limit[start]')
    if (startLimit && moment(startLimit).isAfter(startDate)) {
      startDate = moment(startLimit)
    }

    const date = {
      year: startDate.year(),
      month: startDate.month(),
      day: startDate.date(),
    }
    return moment.tz(date, timezone)
  }

  setStartingDate({ props, skipCount=0, firstAvailableDate }) {
    return new Promise((resolve, reject) => {
      const { day, preselectedDay, timezone, calendarView } = props
      const daysCount = this.getDaysCount({ calendarView })
      const startingDate = this.getStartingDate({ day, preselectedDay, firstAvailableDate, timezone, daysCount }).add(this.state.daysCount * skipCount, 'days')
      this.setState({
        startingDate,
        startingDateISO: startingDate.toISOString(),
      }, () => {
        resolve()
      })
    })
  }

  async fetch() {
    const { startingDate, daysCount } = this.state
    const dateFrom = startingDate
    const dateTo = dateFrom.clone().add(daysCount, 'days')

    this.setState({ loading: true })

    const availableTimes = await this.fetchAvailableTimesRange({ dateFrom, dateTo })

    return new Promise((resolve, reject) => {
      this.setState({
        availableTimes,
        loading: false,
        fetchedTimes: { from: dateFrom, to: dateTo, }
      }, () => {
        resolve()
      })
    })
  }

  async fetchAvailableTimesRange({ dateFrom, dateTo }){
    try {
      const { fetchData, rescheduling } = this.props
      const { serviceId, duration, staffId } = fetchData
      const reschedulingId = rescheduling ? rescheduling.id : null

      const totalDays = Math.ceil(dateTo.diff(dateFrom, 'days', true))

      // Build array of fetch available times promises
      let i = 0
      let fetchPromises = []
      const maxFetchDays = window.MAX_FETCH_DAYS || MAX_FETCH_DAYS
      do {
        const from = dateFrom.clone().add(i, 'days')
        const to = dateFrom.clone().add(i + maxFetchDays, 'days')

        fetchPromises.push(
          fetchAvailableTimes({
            serviceId,
            duration,
            dateFrom: from,
            dateTo: to > dateTo ? dateTo : to,
            staffsIds: staffId ? [staffId] : undefined,
            reschedulingId,
            useUTCEndpoint: true,
          })
        )

        i += maxFetchDays
      } while(i < totalDays)

      let allAvailableTimes = [].concat(...(await Promise.all(fetchPromises)))

      return allAvailableTimes
    } catch(error) {
      console.error('fetchAvailableTimes()', error)
      return new Promise((resolve, reject) => {
        this.setState({ loading: false }, () => {
          resolve()
        })
      })
    }
  }

  onNav(direction){
    const { calendarView } = this.props
    const { startingDate, daysCount } = this.state

    let newStartingDate = startingDate.clone()

    newStartingDate = direction === "next"
      ? startingDate.clone().add(daysCount, 'days').startOf('day')
      : startingDate.clone().subtract(daysCount, 'days').startOf('day')

    this.setState({
      startingDate: newStartingDate,
      startingDateISO: newStartingDate.toISOString(),
      daysCount: this.getDaysCount({ calendarView }),
      loading: true,
    }, () => {
      this.fetch()
    })
  }

  getDaysCount({ calendarView }){
    if(calendarView === "MONTH") {
      return 28
    }

    // Default is week view, which is always 7
    return 7
  }

  getDayAvailableTimes(day, availableTimes) {
    if (! day) {
      return []
    }
    return availableTimes.filter(slot => {
      return moment(slot.time).isBetween(day, day.clone().add(1, 'day'), 'minute', '[)')
        && slot.staffs.length > 0 // DEPRECATE: New resolver just won't return a slot
    })
  }

  maxAvailabilityLookupDateFor(business, serviceId) {
    const service = business.services.find(s => s.id === serviceId)

    let maxLookupDate = moment().add(DEFAULT_BUSINESS_AVAILABILITY_LIMIT_DAYS, 'days')
    if (service.availabilityLimitDays) {
      maxLookupDate = moment().add(service.availabilityLimitDays, 'days')
    } else if (business.availabilityLimitDays) {
      maxLookupDate = moment().add(business.availabilityLimitDays, 'days')
    }
    
    const endLimit = getDateUrlParameter(window.location.search, 'limit[end]')
    if (endLimit && moment(endLimit).isBefore(maxLookupDate)) {
      maxLookupDate = moment(endLimit)
    }
    return maxLookupDate.toISOString()
  }

  render() {
    const { business, fetchData, rescheduling, preselectedDay, day, calendarView } = this.props
    const { startingDate, availableTimes = [], loading, daysCount } = this.state
    const allowDaySelection = preselectedDay ? false : true

    if (loading) {
      return <Loading size="small" relative={true} />
    } else if (!day) {
      return (
        <DaySelection
          availableTimes={availableTimes}
          business={business}
          rescheduling={rescheduling}
          startingDate={startingDate}
          calendarView={calendarView}
          daysCount={daysCount}
          onNav={this.onNav}
          fetchData={fetchData}
          loading={loading} // TODO: Redux will take care of loading state across components
          maxAvailabilityLookupDate={this.maxAvailabilityLookupDateFor(business, fetchData.serviceId)}
        />)
    } else {
      const dayAvailableTimes = this.getDayAvailableTimes(moment(day), availableTimes)
      return (
        <TimeSelection
          business={business}
          rescheduling={rescheduling}
          fetchData={fetchData}
          setCustomTitle={this.props.setCustomTitle}
          allowDaySelection={allowDaySelection}
          availableTimes={dayAvailableTimes}
          onTimeSelected={this.props.onTimeSelected}
        />)
    }
  }
}

const mapStateToProps = (state) => {
  const preselectedDay = (state.page && state.page.booking_date) || null

  return {
    preselectedDay,
    day: preselectedDay || state.day,
    timezone: state.timezone,
  }
}
export default connect(mapStateToProps)(MonthView)
